import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import AgoraRTC from 'agora-rtc-sdk-ng';
import { filter, Subscription } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState, selectUser } from '../../../store/app.selectors';

import { AlertModalComponent } from '../../shared/components/alert/alert.component';
import { scrollToPosition, setLoadingChannel, setLoadingChannels } from '../../shared/store/ui.actions';
import { IUserProfile } from '../../user/interfaces/IUserProfile';
import { AuthenticationService } from '../../user/services/authentication.service';
import { setUser } from '../../user/store/user.actions';
import { CallReciveModalComponent } from '../components/call-recive/call-recive.component';
import { IAttachment } from '../interfaces/IAttachment';
import { ChannelStatus, IChannel, IChannelFilter, ISocketEvent } from '../interfaces/IChannel';
import { DeliveryStatusTypesOptions, IMessage, ISocketMessage } from '../interfaces/IMessage';
import { IMessageableEntity } from '../interfaces/IMessageableEntity';
import { IVideoCallSettings } from '../interfaces/IVideoCallMember';
import { IWorkspace, WorkspaceFeatures } from '../interfaces/IWorkspace';
import {
  getChannel,
  getChannelLang,
  getSmartReplies,
  getStandardReplies,
  loadChannels,
  setAllChannelsRead,
  setBotMode,
  setChannels,
  setChannelStatusEnabled,
  setLastMessage,
  setLoadingSmartReplies,
  applyMessageReaction,
  setReadChannel,
  setSmartReplies,
  unsetMessage,
} from '../store/chat.actions';
import { AppStateWithChat } from '../store/chat.reducer';
import { ChannelsService } from './channels.service';
import { MessagesService } from './messages.service';
import { RxStompService } from './messaging-ws.service';
import { PermissionsService } from './permissions.service';
import { SoundsService } from './sounds.service';

@Injectable()
export class WebsocketService {
  channels: IChannel[];
  profileInfo: IUserProfile | null = null;
  selectedChannel: IChannel | null = null;
  showScrollButton: boolean;
  subscriptions: string[] = [];
  user$ = this.store.select(selectUser);
  videoCall: IVideoCallSettings | null = null;
  websocketDealerSubscription: Subscription;
  websocketUserSubscription: Subscription;
  scrollPosition: number;
  selectedWorkspace: IWorkspace | null = null;
  showChannels: boolean;
  channelsFilter: IChannelFilter;
  users: IMessageableEntity[];

  constructor(
    private soundsService: SoundsService,
    private store: Store<AppStateWithChat>,
    public dialog: MatDialog,
    public messagingWs: RxStompService,
    private messagesService: MessagesService,
    private permissionsService: PermissionsService,
    private channelsService: ChannelsService,
    private authenticationService: AuthenticationService
  ) {
    this.getChatSubscriptions();
    this.getUISubscriptions();
    this.getUserSubscriptions();
    AgoraRTC.setLogLevel(1);
  }

  getChatSubscriptions() {
    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ channels, channelsFilter, selectedChannel, videoCall, users, selectedWorkSpace }) => {
        this.selectedChannel = selectedChannel;
        this.videoCall = videoCall;
        this.channels = channels;
        this.selectedWorkspace = selectedWorkSpace;
        this.channelsFilter = channelsFilter;
        this.users = users;
      });
  }

  getUISubscriptions(): void {
    this.store.select(selectUiState).subscribe(({ showScrollButton, showChannels, scrollPosition }) => {
      this.showScrollButton = showScrollButton;
      this.scrollPosition = scrollPosition;
      this.showChannels = showChannels;
    });
  }

  getUserSubscriptions(): void {
    this.user$.subscribe(async (user) => {
      this.profileInfo = user;
      if (!this.profileInfo) {
        this.subscriptions = [];
      } else {
        this.socketConnectionMessagesDealer();
        this.socketConnectionMessagesUser();
        this.socketConnectionEventsUser();
        this.socketConnectionEventsDealer();
      }
    });
  }

  // subscriptions messages

  socketConnectionMessagesDealer() {
    if (this.profileInfo?.subAccountMessageableEntity?.businessReference?.entityUID) {
      const url = `/messaging/messages/dealer/${this.profileInfo?.subAccountMessageableEntity?.businessReference?.entityUID}`;
      if (!this.subscriptions.find((e) => e === url)) {
        this.subscriptions.push(url);
        this.websocketDealerSubscription = this.messagingWs.watch(url).subscribe((message: { body: string }) => {
          this.setSocketMessage(message);
        });
      }
    }
  }

  socketConnectionMessagesUser() {
    if (this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
      const url = `/messaging/messages/user/${this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID}`;
      if (!this.subscriptions.find((e) => e === url)) {
        this.subscriptions.push(url);
        this.websocketUserSubscription = this.messagingWs.watch(url).subscribe((message: { body: string }) => {
          this.setSocketMessage(message);
        });
      }
    }
  }

  // subscriptions events

  socketConnectionEventsDealer() {
    if (this.profileInfo?.subAccountMessageableEntity?.businessReference?.entityUID) {
      const url = `/messaging/events/dealer/${this.profileInfo?.subAccountMessageableEntity?.businessReference?.entityUID}`;
      if (!this.subscriptions.find((e) => e === url)) {
        this.subscriptions.push(url);
        this.websocketDealerSubscription = this.messagingWs.watch(url).subscribe((event: { body: string }) => {
          this.setSocketEvent(event);
        });
      }
    }
  }

  socketConnectionEventsUser() {
    if (this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
      const url = `/messaging/events/user/${this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID}`;
      if (!this.subscriptions.find((e) => e === url)) {
        this.subscriptions.push(url);
        this.websocketUserSubscription = this.messagingWs.watch(url).subscribe((event: { body: string }) => {
          this.setSocketEvent(event);
        });
      }
    }
  }

  setSocketEvent(event: { body: string }) {
    if (event.body && this.selectedWorkspace) {
      const body = JSON.parse(event.body) as string;
      const bodyJson = JSON.parse(body) as ISocketEvent;
      if (bodyJson?.channel?.businessReference?.module) {
        if (this.selectedWorkspace.id.toUpperCase() === bodyJson.channel.businessReference?.module.toUpperCase()) {
          switch (bodyJson.type) {
            case 'BOT_MODE_CHANGE':
              this.store.dispatch(setBotMode({ channel: bodyJson.channel, value: bodyJson.channel.isBotMode ?? false }));
              break;
          }
        }
      }
    }
  }

  setSocketMessage(message: { body: string }) {
    if (message.body) {
      const body = JSON.parse(message.body) as string;
      const bodyJson = JSON.parse(body) as ISocketMessage;
      if ((bodyJson?.channel?.businessReference?.module || bodyJson?.filters?.module) && this.selectedWorkspace?.id) {
        if (
          this.selectedWorkspace.id.toUpperCase() === bodyJson?.channel?.businessReference?.module?.toUpperCase() ||
          this.selectedWorkspace.id.toUpperCase() === bodyJson?.filters?.module?.toUpperCase()
        ) {
          switch (bodyJson.type) {
            case 'NEW_MESSAGE':
              this.setNewMessage(bodyJson);
              break;
            case 'MESSAGE_READ':
              this.messagesService.setMesageDeliveryStatus(bodyJson, 'read');
              break;
            case 'MESSAGE_DELIVERED':
              this.messagesService.setMesageDeliveryStatus(bodyJson, 'delivered');
              break;
            case 'MESSAGE_FAILED':
              this.messagesService.setMesageDeliveryStatus(bodyJson, 'failed');
              break;
            case 'MEMBER_UNREAD':
              this.setReadChannel(bodyJson, true);
              break;
            case 'MEMBER_READ':
              this.setReadChannel(bodyJson, false);
              break;
            case 'OPT_IN_OUT':
              this.setOptinChannel(bodyJson);
              break;
            case 'REACTION':
              if (bodyJson.reaction) {
                this.store.dispatch(
                  applyMessageReaction({
                    messageUID: bodyJson.message.uid,
                    channelUID: bodyJson.channel.uid,
                    reaction: bodyJson.reaction,
                  })
                );
              }
              break;
            case 'MESSAGE_DELETED':
              this.store.dispatch(unsetMessage({ messageUid: bodyJson.message.uid, channelUid: bodyJson.channel.uid }));
              break;
            case 'CHANNEL_OWNER_CHANGE':
              this.channelOwnerChange(bodyJson);
              break;
            case 'BULK_MARK_MEMBERS_AS_READ':
              this.readAllMessages();
              break;
          }
        }
      } else {
        switch (bodyJson.type) {
          case 'AWAY_START':
            this.startAway(bodyJson);
            break;
          case 'AWAY_END':
            this.finishAway(bodyJson);
            break;
        }
      }
    }
  }

  readAllMessages() {
    this.store.dispatch(setAllChannelsRead());
  }
  channelOwnerChange(data: ISocketMessage) {
    const channelUID = data?.channel?.uid;
    if (channelUID) {
      const channel = this.channels.find((e) => e.uid === channelUID);
      if (channel) {
        this.store.dispatch(
          getChannel({
            channelUID,
            autoSelect: channelUID === this.selectedChannel?.uid,
          })
        );
      } else {
        if (channelUID === this.selectedChannel?.uid) {
          this.store.dispatch(
            getChannel({
              channelUID,
              autoSelect: true,
            })
          );
        }
      }
    }
  }

  startAway(data: ISocketMessage): void {
    if (data?.awaySettings?.delegateUID === this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
      if (this.selectedWorkspace?.id) {
        const user = this.users.find((e) => e.businessReference?.entityUID == data.userEntityUID);
        this.dialog.open(AlertModalComponent, {
          data: {
            hideCancel: true,
            title: 'Away started',
            message: `Advisor ${user?.displayName} has assigned him the responsibility of covering him while he is not available.`,
          },
        });
      }
    } else {
      if (data.userEntityUID === this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
        this.authenticationService.checkData(this.profileInfo?.dealerSettings ?? []);
      }
    }
  }

  finishAway(data: ISocketMessage): void {
    if (data?.awaySettings?.delegateUID === this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
      if (this.selectedWorkspace?.id) {
        const user = this.users.find((e) => e.businessReference?.entityUID == data.userEntityUID);
        if (user) {
          this.dialog.open(AlertModalComponent, {
            data: {
              hideCancel: true,
              title: 'Away finished',
              message: 'The channels have been returned to their original owner, ' + user?.displayName,
            },
          });
        }
        this.reloadChannels();
      }
    } else {
      if (data.userEntityUID === this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID) {
        this.store.dispatch(setUser({ profileInfo: { ...this.profileInfo!, away: undefined } }));
        this.reloadChannels();
      }
    }
  }

  reloadChannels(): void {
    if (this.selectedWorkspace?.id) {
      this.store.dispatch(setLoadingChannels({ value: true }));
      this.store.dispatch(setLoadingChannel({ value: true }));
      this.store.dispatch(
        loadChannels({
          autoSelectChannel: false,
          setLoading: true,
          concat: false,
        })
      );
      if (this.selectedChannel?.uid) {
        this.store.dispatch(
          getChannel({
            channelUID: this.selectedChannel.uid,
            autoSelect: true,
          })
        );
      }
    }
  }

  setReadChannel(bodyJson: ISocketMessage, value: boolean): void {
    this.store.dispatch(setReadChannel({ channel: bodyJson.channel, value }));
  }

  setOptinChannel(bodyJson: ISocketMessage): void {
    this.store.dispatch(setChannelStatusEnabled({ value: bodyJson.enabled, channelUID: bodyJson.channel.uid }));
  }

  setNewMessage(bodyJson: ISocketMessage) {
    let channel = this.channels.find((channel) => bodyJson.channel.uid === channel.uid);
    if (!channel && bodyJson.channel.uid === this.selectedChannel?.uid) {
      channel = this.selectedChannel;
      // border case: channel is selected but its not the channels list
      // modify current channel and add it manually
      this.store.dispatch(setChannels({ channels: [channel, ...this.channels], concat: false }));
    }
    if (channel) {
      channel = { ...channel };
      let attachments: IAttachment[] = [];
      let outcoming = bodyJson.message.outcoming;
      if (bodyJson.message.attachments) {
        attachments = bodyJson.message.attachments;
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.useDealerOutcoming])) {
        outcoming = bodyJson.message.createdBy.uid === this.profileInfo?.subAccountMessageableEntity?.uid;
      }
      let newMessage: IMessage = {
        outcoming: outcoming,
        body: bodyJson.message.body,
        uid: bodyJson.message.uid,
        createdBy: bodyJson.createdBy,
        createdAt: new Date(),
        deliveryStatus: DeliveryStatusTypesOptions.sent,
        attachments: attachments,
        systemTags: bodyJson.message.systemTags,
        origin: bodyJson.message.origin,
      };
      newMessage = this.messagesService.setMessagesOwnership([newMessage])[0];
      if (!outcoming) {
        channel.unreadMessages = (channel.unreadMessages ?? 0) + 1;
      }
      this.setMessage(newMessage, bodyJson.channel);
      if (!outcoming) {
        channel = {
          ...channel,
          messages: [...channel.messages, newMessage],
          enabled: true,
          smartReplies: [],
          lang: undefined,
        };
        this.checkSound(channel);
        this.channelsService.changeChannelStatus(channel, ChannelStatus.ACTIVE);
        this.checkScrollToBottom(channel);
        this.store.dispatch(getChannelLang({ channel }));
        this.store.dispatch(getStandardReplies({ channel }));
        this.store.dispatch(setSmartReplies({ channelUID: channel.uid, smartReplies: [] }));
        this.store.dispatch(setLoadingSmartReplies({ value: true, channelUID: channel.uid }));
        this.store.dispatch(getSmartReplies({ channel }));
        this.channelsService.changeWhatsappStatus(channel);
      } else {
        this.channelsService.checkChannelNewOwner(channel, newMessage);
        this.channelsService.checkChannelNewOrderID(channel, newMessage);
      }
    } else {
      if (this.showChannels) {
        this.getChannel(bodyJson?.channel?.uid);
        this.checkSound(bodyJson?.channel);
      }
    }
    this.checkVideoCall({ ...bodyJson.message, createdBy: bodyJson.createdBy });
  }

  getChannel(channelUID: string) {
    this.store.dispatch(
      getChannel({
        channelUID,
        autoSelect: false,
      })
    );
  }

  setMessage(newMessage: IMessage, channel: IChannel): void {
    this.store.dispatch(
      setLastMessage({
        channel,
        message: newMessage,
      })
    );
  }

  checkScrollToBottom(channel: IChannel): void {
    if (!this.showScrollButton && this.selectedChannel?.uid === channel?.uid) {
      this.store.dispatch(scrollToPosition({ value: 1 }));
    }
  }

  checkSound(channel: IChannel): void {
    if (this.showChannels && (this.showScrollButton || this.selectedChannel?.uid !== channel?.uid) && !channel?.isMuted) {
      this.soundsService.playNewMessage();
    }
  }

  checkVideoCall(newMessage: IMessage): void {
    let roomName: string | null = null;
    const videoCallLink = this.selectedWorkspace?.config?.dynamicLinks?.find((e) => e.previewTemplate === 'videoCallModal');
    if (videoCallLink?.pattern) {
      roomName = this.getRoomName(newMessage.body, videoCallLink.pattern);
      if (roomName && roomName !== this.videoCall?.room) {
        this.openDialogCall(newMessage.createdBy, roomName);
      }
    }
  }

  async unsubscribeConnections() {
    await this.messagingWs.deactivate();
    this.subscriptions = [];
    this.websocketDealerSubscription.unsubscribe();
    this.websocketUserSubscription.unsubscribe();
  }

  getRoomName(value: string, pattern: RegExp): string {
    const res = this.getPatternValue(value, pattern);
    return res;
  }

  getPatternValue(value: string, pattern: RegExp): string {
    const regexp = new RegExp(pattern);
    const match = regexp.test(value) ? value.match(regexp) : null;
    return match?.[1] ?? '';
  }

  openDialogCall(client: { uid?: string; displayName?: string }, roomName: string) {
    this.dialog.open(CallReciveModalComponent, {
      width: '230px',
      panelClass: 'custom-call-modal',
      hasBackdrop: false,
      data: {
        client: client,
        roomId: roomName,
      },
      position: {
        bottom: '10px',
        right: '10px',
      },
    });
  }
}
